import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';
import { handleCompleteLogout } from '../utils/auth';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});
const unAuthenticatedApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});
let isRefreshing = false;
let failedQueue = [];
let refreshingPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.request.use(async (req) => {
  if (refreshingPromise) {
    await refreshingPromise;
  }
  let accessToken = Cookies.get('token');

  /* A cookie that isn't a JWT — truncated, cleared mid-write, left over from
     an older login — used to throw here, inside the interceptor, where the
     failure had nowhere to surface: the request was never sent and the screen
     sat on its loader. Treated as "no token": the request goes out and the
     server decides. */
  let claims = {};
  try {
    claims = accessToken ? jwtDecode(accessToken) : {};
  } catch {
    claims = {};
  }

  const expiration = claims?.exp ? new Date(claims?.exp * 1000) : new Date();
  const now = new Date();
  now.setSeconds(now.getSeconds() - 10);

  if (now > expiration) {
    if (!refreshingPromise) {
      refreshingPromise = getRefreshedToken().finally(() => {
        refreshingPromise = null;
      });
    }
    await refreshingPromise;
    accessToken = Cookies.get('token');
  }

  if (
    accessToken &&
    accessToken !== 'null' &&
    accessToken !== 'undefined' &&
    req.headers
  ) {
    req.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return req;
});

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.data?.reason === 'permission_denied') {
      const message = error.response.data.message || 'You do not have permission to do that';
      toast.error(message, { id: `permission-denied:${message}` });
      throw error.response.data;
    }
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = Cookies.get('refresh_token');
      if (!refreshToken && originalRequest._retry) {
        toast.error('Session expired. Please login again.');
        originalRequest._retry = false;
        return handleCompleteLogout();
      }
      try {
        const response = await getRefreshedToken();
        const access_token = response?.access_token;
        if (!access_token) {
          toast.error('Session expired. Please login again.');
          processQueue(error, null);
          return handleCompleteLogout();
        }
        if (Cookies.get('token') !== access_token) {
          toast.error('Session could not be renewed. Please login again.');
          processQueue(error, null);
          return handleCompleteLogout();
        }
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        processQueue(null, access_token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    throw error?.response?.data ?? error?.message;
  }
);

export const getRefreshedToken = async () => {
  const refreshToken = Cookies.get('refresh_token');
  if (refreshToken) {
    try {
      const response = await unAuthenticatedApi.post('/users/token', {
        refresh_token: refreshToken,
      });
      const { access_token, refresh_token } = response.data;
      Cookies.set('token', access_token, { path: '/' });
      if (refresh_token) {
        Cookies.set('refresh_token', refresh_token, { path: '/' });
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return response.data;
    } catch (error) {
      if (error?.message === 'Access Denied') {
        processQueue(error, null);
        toast.error('Session expired. Please login again.');
        handleCompleteLogout();
      } else {
        toast.error(
          error?.message?.message ||
            error?.message ||
            'Unable to refresh session'
        );
      }
      return null;
    }
  }
};

unAuthenticatedApi.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    throw error?.response?.data ?? error?.message;
  }
);
export { unAuthenticatedApi };
export default api;
